import React, { useEffect, useMemo } from 'react';
import { useGoogleInteractableMap } from '@shared/components/JoyInteractableMap/JoyInteractableMap.utils';
import { useJoyInteractableMapContext } from '@shared/components/JoyInteractableMap/JoyInteractableMap.provider';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { MapPinWithCardV2 } from '@shared/components/JoyInteractableMap/MapPin';
import { pxToRem } from '@withjoy/joykit/theme';
import { HotelProduct, MapMarker } from '@withjoy/joykit/icons';
import type { HotelMapPinWithCardProps, MapProps } from './AccommodationsTour.types';
import { LazyJoyInteractableMap } from '@shared/components/JoyInteractableMap';
import type { LatLngLiteral } from '@shared/core/google-maps/types';
import { useTranslation } from '@shared/core';

const CARD_WIDTH = 224;
const EMPTY_MARKERS: ReadonlyArray<LatLngLiteral> = [];

// Tight bounds (~50m) for tour map since hotel and venue pins are always close together
const TOUR_MAP_TIGHT_BOUNDS_PADDING = 0.0005;

/**
 * Renders price information with optional strike-through price
 */
const PriceDisplay = React.memo<{ price: string; strikeOutPrice?: Maybe<string> }>(({ price, strikeOutPrice }) => (
  <Flex
    gap={2}
    paddingX={1}
  >
    {strikeOutPrice && (
      <TextV2
        typographyVariant={'body1'}
        fontSize={pxToRem(14)}
        fontWeight={500}
        textDecorationLine={'line-through'}
        color={'mono8'}
      >
        {strikeOutPrice}
      </TextV2>
    )}
    <TextV2
      typographyVariant={'body1'}
      fontSize={pxToRem(14)}
      fontWeight={600}
      color={'mono14'}
    >
      {price}
    </TextV2>
  </Flex>
));

PriceDisplay.displayName = 'PriceDisplay';

/**
 * Renders venue name with map marker icon
 */
const VenueNameDisplay = React.memo<{ name: string }>(({ name }) => (
  <Flex
    alignItems="center"
    gap={2}
  >
    <MapMarker
      size={16}
      color={'mono14'}
    />
    <TextV2
      typographyVariant={'body1'}
      fontSize={pxToRem(14)}
      fontWeight={600}
      color={'mono15'}
    >
      {name}
    </TextV2>
  </Flex>
));

VenueNameDisplay.displayName = 'VenueNameDisplay';

/**
 * Renders the appropriate label content based on available props
 */
const getPinLabel = (name?: string, price?: Maybe<string>, strikeOutPrice?: Maybe<string>) => {
  if (price) {
    return (
      <PriceDisplay
        price={price}
        strikeOutPrice={strikeOutPrice}
      />
    );
  }

  if (name) {
    return <VenueNameDisplay name={name} />;
  }

  return <HotelProduct size={20} />;
};

/**
 * A map pin component that displays hotel or venue information with an optional price card
 * @param props - Component props
 * @returns React component
 */
const HotelMapPinWithCard = React.memo((props: HotelMapPinWithCardProps) => {
  const { location, forceUpdate, name, price, strikeOutPrice, intent, showOnTop } = props;

  const isMobileOrTablet = useIsMobileOrTablet();

  return (
    <MapPinWithCardV2
      cardStyleOverride={{
        ...(isMobileOrTablet && {
          bottom: '64px',
        }),
        width: pxToRem(CARD_WIDTH),
        height: 'auto',
        padding: pxToRem(8),
        borderRadius: pxToRem(8),
        gap: pxToRem(8),
        flexDirection: 'column',
      }}
      position={{ lat: location.lat, lng: location.lng }}
      isActive={false}
      showOnTop={showOnTop}
      isCardVisible={false}
      onDoubleClick={e => {
        e.stopPropagation();
      }}
      forceUpdate={forceUpdate || isMobileOrTablet.toString()}
      label={getPinLabel(name, price, strikeOutPrice)}
      intent={intent}
    />
  );
});

HotelMapPinWithCard.displayName = 'HotelMapPinWithCard';

/**
 * Map component for tour steps that shows hotels and venue pins
 */
const HotelTourStepMap = (props: MapProps) => {
  const { originList, destination, property } = props;

  const { tq } = useTranslation('travelMap');

  const { map } = useJoyInteractableMapContext();
  const { getCalculatedCenterAndZoom } = useGoogleInteractableMap();

  // Stabilize array reference to prevent bounds recalculation on every render
  const allMarkers = useMemo(() => (originList.length ? [...originList, destination] : EMPTY_MARKERS), [originList, destination]);

  // Compute bounds with tight padding for this specific tour map use case
  const bounds = useMemo(() => {
    if (!getCalculatedCenterAndZoom || !allMarkers.length) return undefined;
    return getCalculatedCenterAndZoom(allMarkers, { minBoundsPadding: TOUR_MAP_TIGHT_BOUNDS_PADDING }).bounds;
  }, [getCalculatedCenterAndZoom, allMarkers]);

  // Refit bounds when markers change after initial mount
  useEffect(() => {
    if (map && bounds) {
      map.fitBounds(bounds);
    }
  }, [map, bounds]);

  if (!bounds) return null;

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      flex={1}
    >
      <LazyJoyInteractableMap
        center={destination}
        bounds={bounds}
        autoCenterAndZoom
        gestureHandling="cooperative"
      >
        <HotelMapPinWithCard
          price={property.price}
          strikeOutPrice={property.strikeOutPrice}
          location={originList[0]}
          intent={'aiBorder'}
        />
        <HotelMapPinWithCard
          name={tq`Your Venue`}
          location={destination}
          intent={'noBorder'}
          showOnTop
        />
      </LazyJoyInteractableMap>
    </Box>
  );
};

HotelTourStepMap.displayName = 'HotelTourStepMap';

export { HotelTourStepMap };
